<script setup>
import {ref, computed, watch, onMounted, defineEmits, defineProps} from "vue";
import {useProductRangeStore} from "@/stores/ProductRange.js";
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod";
import axios from "axios";


const props = defineProps({
    value: {type: [Number, Array], default: null},
    multiple: {type: Boolean, default: false},
    label: {type: String, default: ""},
    color: {type: String, default: ""},
    disabled: {type: Boolean, default: false},
    readonly: {type: Boolean, default: false},
    notClearable: {type: Boolean, default: false},
    rules: {type: Array, default: () => []},
    hint: {type: String, default: ""},
    persistentHint: {type: Boolean, default: false},
});

const emit = defineEmits(['input']);

const {transformFilterValues} = useSharedMethod();
const {filter, loading, inputColor, backgroundColor} = useSharedData();

const productRangeStore = useProductRangeStore();
const productRange = ref(props.multiple ? [] : null);

// ── sync v-model ──────────────────────────────────────────────────────
watch(() => props.value, (val) => {
    productRange.value = val;
}, {immediate: true});

// ── select-all helpers ────────────────────────────────────────────────
const allSelected = computed(() =>
    productRangeStore.productRanges.length > 0 &&
    Array.isArray(productRange.value) &&
    productRange.value.length === productRangeStore.productRanges.length
);

const someSelected = computed(() =>
    Array.isArray(productRange.value) &&
    productRange.value.length > 0 &&
    !allSelected.value
);

function toggleAll() {
    productRange.value = allSelected.value
        ? []
        : productRangeStore.productRanges.map((i) => i.id);
    emit("input", productRange.value);
}

function clearAll() {
    productRange.value = [];
    emit("input", productRange.value);
}

function removeItem(item) {
    productRange.value = productRange.value.filter((id) => id !== item.id);
    emit("input", productRange.value);
}

function onInput() {
    emit("input", productRange.value);
}

// ── API ───────────────────────────────────────────────────────────────
function findAll() {
    loading.value = true;
    filter.value.allRecords = true;
    axios.get("/productRanges", {params: transformFilterValues(filter.value)}).then((response) => {
        productRangeStore.setProductRanges(response.data);
    }).finally(() => {
        loading.value = false;
    });
}

onMounted(() => findAll());

</script>

<template>
    <v-autocomplete
        v-model="productRange"
        :label="label"
        :items="productRangeStore.productRanges"
        item-value="id"
        item-text="name"
        outlined
        dense
        :clearable="!readonly && !multiple && !notClearable"
        :chips="multiple"
        :deletable-chips="multiple"
        :color="color ? color : inputColor"
        :background-color="color ? color + ' lighten-5' : backgroundColor"
        hide-details="auto"
        :loading="loading"
        :disabled="disabled"
        :readonly="readonly"
        :multiple="multiple"
        :rules="rules"
        :hint="hint"
        :persistent-hint="persistentHint"
        @input="onInput"
    >
        <!-- Select-all row — only when multiple -->
        <template v-if="multiple" #prepend-item>
            <v-list-item ripple @mousedown.prevent @click="toggleAll">
                <v-list-item-action>
                    <v-icon :color="(allSelected || someSelected) ? (color || 'teal') : ''">
                        {{
                            allSelected ? 'mdi-checkbox-marked' :
                                someSelected ? 'mdi-minus-box' :
                                    'mdi-checkbox-blank-outline'
                        }}
                    </v-icon>
                </v-list-item-action>
                <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">Tous</v-list-item-title>
                </v-list-item-content>
            </v-list-item>
            <v-divider class="mt-0 mb-0"/>
        </template>

        <!-- Custom chip rendering -->
        <template v-if="multiple" #selection="{ item, index }">
            <!-- All selected → single "Tous" chip -->
            <template v-if="allSelected">
                <v-chip
                    v-if="index === 0"
                    close
                    small
                    :color="color ? color : 'teal'"
                    class="white--text"
                    @click:close="clearAll"
                >
                    Tous
                </v-chip>
            </template>

            <!-- Partial selection → individual chips -->
            <v-chip
                v-else
                close
                small
                @click:close="removeItem(item)"
            >
                {{ item.name }}
            </v-chip>
        </template>
    </v-autocomplete>
</template>